import { CheckOutlined, InfoCircleFilled } from '@ant-design/icons';
import { useId } from 'react';

import { resolveOptionIcon } from './optionIcons';

export type OptionCard = {
  value: string;
  label: string;
  /** Optional supporting line under the label. */
  description?: string | null;
  /** Registry key — resolved through `optionIcons.ts`. Unknown/unset keys
   *  render the card without an icon rather than erroring. */
  icon?: string | null;
  /** Per-option help bubble, shown as an info icon beside the label. Both
   *  halves must be present — the list layout enforces the same pairing. */
  helpTitle?: string | null;
  helpBody?: string | null;
};

type Props = {
  options: OptionCard[];
  /** Single-select (`RadioGroup`) passes a string; multi-select
   *  (`CheckboxGroup`) passes the checked array. */
  value: string | string[] | undefined;
  /** True for CheckboxGroup — switches the control to a square tick and lets
   *  more than one card be active at once. */
  multiple?: boolean;
  onChange: (next: string | string[]) => void;
  disabled?: boolean;
  /** Radio inputs need a shared name to behave as one group. Auto-generated
   *  when omitted. */
  name?: string;
  /** Called when an option's help icon is activated. The caller owns the
   *  modal so this component stays presentational. */
  onOptionHelp?: (help: { title: string; body: string }) => void;
};

/**
 * Card layout for option groups — the 2026-07-24 BMO design. Each option is a
 * bordered tile with its selection control pinned top-right, an optional icon
 * centred above the label, and the label (plus optional description) centred
 * beneath.
 *
 * Rendered instead of the plain list when the field sets
 * `displayAs: 'card'`. The whole card is the click target: the native input
 * is visually hidden inside the `<label>`, so keyboard focus, Space/Enter
 * activation, and screen-reader semantics all come free from the browser
 * rather than being re-implemented with role/tabIndex/onKeyDown.
 *
 * Visual treatment lives in `.bmo-option-card*` in global.css and reuses the
 * same tokens as `.bmo-radio` / `.bmo-checkbox` (24px control, 2px #646c76
 * border, #0075be accent) so cards sit alongside the list controls without
 * drifting.
 */
export function OptionCardGroup({
  options,
  value,
  multiple = false,
  onChange,
  disabled,
  name,
  onOptionHelp,
}: Props) {
  const generated = useId();
  const groupName = name ?? generated;
  const checkedValues = multiple
    ? ((value as string[] | undefined) ?? [])
    : value
      ? [value as string]
      : [];

  const toggle = (optionValue: string, checked: boolean): void => {
    if (!multiple) {
      onChange(optionValue);
      return;
    }
    const next = new Set(checkedValues);
    if (checked) next.add(optionValue);
    else next.delete(optionValue);
    onChange(Array.from(next));
  };

  return (
    <div className="bmo-option-card-group" role={multiple ? 'group' : 'radiogroup'}>
      {options.map((o) => {
        const icon = resolveOptionIcon(o.icon);
        const isChecked = checkedValues.includes(o.value);
        return (
          <label className="bmo-option-card" key={o.value}>
            <input
              type={multiple ? 'checkbox' : 'radio'}
              name={groupName}
              value={o.value}
              checked={isChecked}
              disabled={disabled}
              onChange={(e) => toggle(o.value, e.target.checked)}
            />
            {/* Control pinned top-right. Circle for single-select, square
                with a tick for multi — mirrors the list-layout controls so a
                card group reads as the same kind of question. */}
            <span
              className={
                multiple
                  ? 'bmo-option-card__control bmo-option-card__control--box'
                  : 'bmo-option-card__control'
              }
            >
              {multiple ? (
                <CheckOutlined className="bmo-option-card__tick" />
              ) : (
                <span className="bmo-option-card__dot" />
              )}
            </span>
            {icon && (
              // Decorative: the label immediately below carries the meaning,
              // so an alt would just be read twice by a screen reader.
              <img className="bmo-option-card__icon" src={icon.src} alt="" aria-hidden />
            )}
            <span className="bmo-option-card__label">
              {o.label}
              {o.helpTitle && o.helpBody && (
                // Stops the click bubbling to the <label>, which would
                // otherwise select the card just because the advisor wanted
                // to read what the option means.
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={o.helpTitle}
                  className="bmo-option-card__help"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onOptionHelp?.({ title: o.helpTitle!, body: o.helpBody! });
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    e.stopPropagation();
                    onOptionHelp?.({ title: o.helpTitle!, body: o.helpBody! });
                  }}
                >
                  <InfoCircleFilled />
                </span>
              )}
            </span>
            {o.description && <span className="bmo-option-card__desc">{o.description}</span>}
          </label>
        );
      })}
    </div>
  );
}
